import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { CLINIC_LOGOS_QUERY_KEY } from '@/hooks/useClinicLogos';
import type { Database, Json } from '@/types/database.generated';

export type ClinicRow = Database['public']['Tables']['clinics']['Row'];

export const clinicQueryKey = (clinicId: string | null | undefined) =>
  ['clinic', clinicId ?? null] as const;

/**
 * Loads the active clinic by id. Returns null if `clinicId` is falsy.
 * RLS already restricts visibility to clinic members + platform admins, so the
 * shape of the query is intentionally trivial.
 */
export function useClinic(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: clinicQueryKey(clinicId),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<ClinicRow | null> => {
      if (!clinicId) return null;
      const { data, error } = await supabase
        .from('clinics')
        .select('*')
        .eq('id', clinicId)
        .single();
      if (error) {
        if (error.code === 'PGRST116') return null;
        throw error;
      }
      return data as ClinicRow;
    },
    staleTime: 60_000,
  });
}

/**
 * Mutates clinic settings via the public.update_clinic_settings RPC. The RPC
 * authorizes the caller (clinic admin or platform admin) and writes audit rows
 * atomically. Updates the React Query cache with the returned row on success.
 */
export function useUpdateClinicSettings(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Record<string, Json>): Promise<ClinicRow> => {
      if (!clinicId) throw new Error('No active clinic');
      const { data, error } = await supabase.rpc('update_clinic_settings', {
        p_clinic_id: clinicId,
        p_payload: payload as Json,
      });
      if (error) throw error;
      return data as ClinicRow;
    },
    onSuccess: (row) => {
      qc.setQueryData(clinicQueryKey(clinicId ?? null), row);
      void qc.invalidateQueries({ queryKey: [CLINIC_LOGOS_QUERY_KEY] });
    },
  });
}

/**
 * Toggles a single key in `activated_modules`. Platform admin only — the RPC
 * itself enforces this and returns 'forbidden' otherwise.
 */
export function useToggleClinicModule(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      moduleKey,
      enabled,
    }: {
      moduleKey: string;
      enabled: boolean;
    }): Promise<ClinicRow> => {
      if (!clinicId) throw new Error('No active clinic');
      const { data, error } = await supabase.rpc('toggle_clinic_module', {
        p_clinic_id: clinicId,
        p_module_key: moduleKey,
        p_enabled: enabled,
      });
      if (error) throw error;
      return data as ClinicRow;
    },
    onSuccess: (row) => {
      qc.setQueryData(clinicQueryKey(clinicId ?? null), row);
    },
  });
}
